import React, { FC } from 'react';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material';
import parse from 'html-react-parser';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import ConditionalLink from '~/components/ConditionalLink/ConditionalLink';
import FollowButton from '~/components/buttons/FollowButton';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useFollowExperts from '~/hooks/useFollowExperts';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import {
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN,
  SMALL_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { User } from '~/types';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

interface IExpertInfo {
  id: string;
  name: string;
  bio: User['description'];
  link: string;
  restrictNavigation: boolean;
  loading: boolean;
}

const ExpertInfo: FC<Partial<IExpertInfo>> = ({
  id,
  name,
  bio,
  link,
  restrictNavigation,
  loading,
  ...rest
}) => {
  const { followed, toggleFollowing, loading: loadingToggleFollowing } = useFollowExperts([id]);
  const { userInfo } = useUserInfoContext();

  const handleClickFollow = () => {
    if (!followed) {
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.follow);
    }
    toggleFollowing();
  };

  if (loading) {
    return (
      <Wrapper {...rest}>
        <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
        <AnimatedSkeletonMultiline
          xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
          spacer={TINY_MARGIN_PX}
          lines={3}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...rest}>
      <GeneratedExpertTitleLink withLink={!restrictNavigation} to={link} data-testid="expertLink">
        <Title data-testid="expertLinkContent" variant="h3">
          {name}
        </Title>
      </GeneratedExpertTitleLink>
      {!restrictNavigation && !!userInfo && (
        <StyledFollowButton
          loading={loadingToggleFollowing}
          followed={followed}
          onClick={handleClickFollow}
        />
      )}
      {bio && <Description>{parse(bio)}</Description>}
    </Wrapper>
  );
};

export default ExpertInfo;

const Wrapper = styled(Box)`
  flex: 1;
  width: 100%;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: unset;
  }
`;
const GeneratedExpertTitleLink = styled(ConditionalLink)`
  color: ${({ theme }) => theme.palette.common.white};
  transition: 0.3s color;

  &:hover {
    color: ${({ theme }) => theme.palette.common.expertHeader};
  }
`;
const Title = styled(Typography)`
  font-size: 18px;
  line-height: 1.4;
  text-align: center;

  & {
    margin-bottom: ${SMALL_MARGIN}px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 22px;
    text-align: left;
    line-height: 1;
  }
`;
const Description = styled(Box)`
  font-size: 16px;
  line-height: 1.5;
  white-space: break-spaces;
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
  }
`;
const LoadingTitle = styled(AnimatedSkeleton)`
  max-width: 200px;
  margin-bottom: ${SMALL_MARGIN_PX};
`;
const StyledFollowButton = styled(FollowButton)`
  margin-bottom: ${TINY_MARGIN_PX};
  margin-left: auto;
  margin-right: auto;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
    margin-left: 0;
  }
`;
